import {FC, useCallback, useEffect, useState} from 'react';
import {styled} from 'styled-components';
import {useLocation} from 'wouter';

import {SUPPORTED_LANGUAGES, SupportedLanguage} from '@shared/models/language';

import {Button} from '@shared_web/components/core/button';
import {Input} from '@shared_web/components/core/input';
import {hideModal, showModal} from '@shared_web/components/core/modal';
import {showAlert, showSuccess} from '@shared_web/components/core/notifications';
import {notifyError} from '@shared_web/lib/notification';

import {Line, TabWrapper} from '@mavismart_frontend/components/common_styles';
import {GenericConfirmModal} from '@mavismart_frontend/components/generic_confirm_modal';
import {Loader} from '@mavismart_frontend/components/loader';
import {Title} from '@mavismart_frontend/components/title';
import {ToggleSwitch} from '@mavismart_frontend/components/toggle_switch';
import {translate, Translate} from '@mavismart_frontend/components/translate';
import {apiCall} from '@mavismart_frontend/lib/api';
import {
  BORDER_COLOR,
  BUTTON_THEME,
  BUTTON_THEME2,
  INPUT_THEME,
  MODAL_THEME,
  MODAL_TITLE_STYLE,
  SELECT_THEME,
  TEXT_MAIN_COLOR,
} from '@mavismart_frontend/lib/constants';
import {resetStores} from '@mavismart_frontend/lib/init_reset_store';
import {getUserLanguage, setAccount, useAccount} from '@mavismart_frontend/stores/account_store';

export const UserAccount: FC = () => {
  const account = useAccount();
  const [, navigate] = useLocation();

  const [phone, setPhone] = useState(account?.phone ?? '');
  const [language, setLanguage] = useState(getUserLanguage(account));
  const [hasSupport, setHasSupport] = useState(
    account?.hasSupportApp === true && account?.supportAppEnabled === true
  );

  useEffect(() => {
    setPhone(account?.phone ?? '');
    setLanguage(getUserLanguage(account));
    setHasSupport(account?.hasSupportApp === true && account?.supportAppEnabled === true);
  }, [account]);

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value);
  }, []);

  const handleLanguageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setLanguage(e.target.value as SupportedLanguage);
  }, []);

  const handleSupportToggleChange = useCallback((checked: boolean) => {
    setHasSupport(checked);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const updatedAccount = await apiCall('POST /update-account', {
        phone,
        language,
        supportAppEnabled: hasSupport,
      });

      setAccount({
        ...updatedAccount,
        supportAppEnabled: hasSupport,
        language: updatedAccount.language ?? getUserLanguage(undefined),
      });

      showSuccess(translate('account_updated', getUserLanguage(account)));
    } catch {
      showAlert(translate('error_updating_account', getUserLanguage(account)));
    }
  }, [phone, language, account, hasSupport]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await apiCall('POST /delete-account', {});
      resetStores();
      hideModal();
      navigate('/login');
    } catch (err) {
      notifyError(err);
    }
  }, [navigate]);

  const handleDeleteAccount = useCallback(async () => {
    showModal({
      title: (
        <MODAL_TITLE_STYLE>
          {translate('delete_account_warning', getUserLanguage(account))}
        </MODAL_TITLE_STYLE>
      ),
      ...MODAL_THEME,
      children: <GenericConfirmModal onConfirm={handleDeleteConfirm} />,
    });
  }, [account, handleDeleteConfirm]);

  const handleLogOut = useCallback(async () => {
    try {
      await apiCall('POST /logout', {});
      navigate('/login');
      resetStores();
    } catch {
      throw new Error('Logout failed');
    }
  }, [navigate]);

  const showModalLogout = useCallback(() => {
    showModal({
      title: (
        <MODAL_TITLE_STYLE>
          {translate('logout_warning', getUserLanguage(account))}
        </MODAL_TITLE_STYLE>
      ),
      ...MODAL_THEME,
      children: <GenericConfirmModal onConfirm={handleLogOut} />,
    });
  }, [account, handleLogOut]);

  if (account === undefined) {
    return <Loader />;
  }

  return (
    <TabWrapper style={{justifyContent: 'space-between'}}>
      <WrapperTop>
        <Title name={<Translate value={'user_account'} />} />
        <Input
          width={'100%'}
          label={translate('email', getUserLanguage(account))}
          value={account.email}
          readOnly
          themeOverrides={{input: INPUT_THEME}}
        />
        <Input
          type="tel"
          autoComplete="tel"
          width={'100%'}
          label={translate('phone', getUserLanguage(account))}
          value={phone}
          onChange={handlePhoneChange}
          themeOverrides={{input: INPUT_THEME}}
        />
        <div>
          <label htmlFor="language-select">{translate('langue', getUserLanguage(account))}</label>
          <SelectWrapper>
            {/** biome-ignore lint/correctness/useUniqueElementIds: TODO: fix this */}
            <Styledselect
              id="language-select"
              name="language"
              value={language}
              onChange={handleLanguageChange}
            >
              {SUPPORTED_LANGUAGES.map(lang => (
                <option key={lang.name} value={lang.name}>
                  {translate(lang.name, getUserLanguage(account))}
                </option>
              ))}
            </Styledselect>
          </SelectWrapper>
        </div>
        {account?.hasSupportApp === true && (
          <WrapperToggle>
            <Line>
              <Translate value="activate_support_app" />
              <ToggleSwitch checked={hasSupport} onChange={handleSupportToggleChange} />
            </Line>
          </WrapperToggle>
        )}
        <Button themeOverrides={BUTTON_THEME} expand onClickAsync={handleSave}>
          <Translate value={'save'} />
        </Button>
      </WrapperTop>
      <BottomButtons>
        {!account.canDeleteAccount ? (
          <></>
        ) : (
          <Button themeOverrides={BUTTON_THEME2} expand onClickAsync={handleDeleteAccount}>
            <Translate value={'delete_account'} />
          </Button>
        )}
        <Button themeOverrides={BUTTON_THEME} expand onClick={showModalLogout}>
          <Translate value={'logout'} />
        </Button>
      </BottomButtons>
    </TabWrapper>
  );
};

UserAccount.displayName = 'UserAccount';

const BottomButtons = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
`;

const WrapperTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Styledselect = styled.select`
  width: 100%;
  height: 50px;
  padding: 10px 40px 10px 12px;
  font-size: 16px;
  ${SELECT_THEME}
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
  border: 1px solid ${BORDER_COLOR};
  border-radius: 8px;

  &:hover {
    border-color: ${TEXT_MAIN_COLOR};
  }
`;
const WrapperToggle = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
`;
